import { useCallback, type ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { useMeQuery } from '@/src/features/auth/services/useMeQuery'
import type { TCommentApi } from '@/src/features/comment/api/types/commentApi.types'
import { TEST_ID } from '@/src/general/constants/testId'
import { ICON } from '@/src/general/enums/icons'

import { useCommentsQuery } from '../services/useCommentsQuery'
import { CommentThread } from './CommentThread'

type TPublicationCommentsProps = {
  publicationId: number
  onShowCommentPressed: VoidFunction
  showComments: boolean
  commentsCount: number
}

const keyExtractor = (item: TCommentApi) => `${item.id}`

export const CommentList = ({
  publicationId,
  onShowCommentPressed,
  showComments,
  commentsCount,
}: TPublicationCommentsProps): ReactNode => {
  const { data, fetchNextPage, isFetchingNextPage, isError, isLoading, hasNextPage } = useCommentsQuery(
    publicationId,
    showComments,
  )

  const { data: me } = useMeQuery()

  const handleEndReached = useCallback(() => {
    if (!isFetchingNextPage && !isError) {
      fetchNextPage()
    }
  }, [isFetchingNextPage, isError, fetchNextPage])

  return (
    <>
      {(!showComments || isLoading) && commentsCount > 0 && (
        <Button
          testID={TEST_ID.COMMENT.LOAD_COMMENTS}
          colors="default"
          variant="faded"
          className="my-4"
          isLoading={isLoading}
          onPress={onShowCommentPressed}
          endIcon={ICON.CHEVRON_DOWN}
        >
          <FormattedMessage id="publication.comment.show" />
        </Button>
      )}
      {!!showComments && (
        <>
          {/* Using map here, as it not an infinite scroll but more like a pagination */}
          <View className="mt-4  w-full">
            {data?.pages.map((item) => (
              <CommentThread key={keyExtractor(item)} comment={item} me={me} publicationId={publicationId} />
            ))}
          </View>
          {/*
            commentsCount > 1 is useful when a comment is created and the list is not fetched yet, 
            so we still want to display the button load more if there are more comments to load
          */}
          {!!hasNextPage && commentsCount > 1 && (
            <Button
              testID={TEST_ID.COMMENT.LOAD_MORE_COMMENTS}
              colors="default"
              variant="faded"
              className="my-4"
              isLoading={isFetchingNextPage}
              onPress={handleEndReached}
            >
              <FormattedMessage id="publication.comment.loadmore" />
            </Button>
          )}
        </>
      )}
      <View className="h-4" />
    </>
  )
}
